import { useState } from "react";
import { X, Loader2, Wallet, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const MIN = 5;
const MAX = 200;
const PRESETS = [10, 25, 50, 100];

// Face-value cash top-up: add $X, get exactly $X of balance. Never expires.
export default function TopUpModal({ open, onClose }) {
  const [amount, setAmount] = useState(25);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const price = Number(amount).toFixed(2);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-custom", { amount_usd: Number(amount), origin_url: window.location.origin });
      if (!data?.url) throw new Error("no-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-[#171426]/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="topup-modal-overlay"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto sms-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="topup-modal"
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#f0eef7]">
          <div>
            <h2 className="text-xl font-extrabold">Add Funds</h2>
            <p className="text-sm text-[#6b6880] mt-0.5">Add cash to your wallet — spend it whenever. Never expires.</p>
          </div>
          <button data-testid="topup-close-btn" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#eef3ea] text-[#6b6880] shrink-0">
            <X size={22} />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="text-center">
            <span className="text-5xl font-extrabold text-[#171426] tabular-nums" data-testid="topup-amount">${price}</span>
            <span className="text-sm text-[#9a96ac] font-semibold ml-1">USD</span>
            <div className="text-[12px] text-[#9a96ac] mt-2">Face value — you get exactly what you add</div>
          </div>

          <input
            type="range" min={MIN} max={MAX} step={5} value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            data-testid="topup-slider"
            className="w-full mt-5 accent-[#5d8a52] cursor-pointer"
          />
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {PRESETS.map((p) => (
              <button
                key={p}
                data-testid={`topup-preset-${p}`}
                onClick={() => setAmount(p)}
                className={`flex items-center gap-1 text-sm font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${amount === p ? "bg-[#eef3ea] border-[#5d8a52] text-[#3f6238]" : "border-[#e3e0ee] text-[#6b6880] hover:border-[#a9c39c]"}`}
              >
                {amount === p && <Check size={13} />}${p}
              </button>
            ))}
          </div>

          <button
            data-testid="topup-checkout"
            onClick={checkout}
            disabled={busy}
            className="w-full mt-6 inline-flex items-center justify-center gap-2 font-extrabold text-white py-3 rounded-2xl transition-colors disabled:opacity-70"
            style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
          >
            {busy ? <><Loader2 size={18} className="spin" /> Starting checkout…</> : <><Wallet size={18} /> Add ${price} to wallet</>}
          </button>
          <p className="text-center text-xs text-[#9a96ac] mt-3">Secure one-time checkout by Stripe · funds added instantly.</p>
        </div>
      </div>
    </div>
  );
}
